import React, { useEffect, useState } from 'react'

import { Button, Input, Select } from '@st-operations/uikit/src/v2/index.js'

import RadioGroup from '_components/radio-input-group'
import { useDestinations } from '_hooks'
import { getCustomAudienceEnum } from '_services/audience'

import ProgressBar from '../../bulk-edit-audiences/components/progress-bar'
import {
  fetchAllAudiences,
  fetchAudiencesByIds,
} from '../../bulk-edit-audiences/lib/load-audiences'
import { MODE_OPTIONS, QUERY_MODES, parseIds } from '../../bulk-edit-audiences/lib/query-modes'
import { COLLECTION } from '../collection'

const EMPTY_FILTERS = {
  created_by: null,
  destination: null,
  status: null,
  vertical: null,
  q: '',
}

const SelectStage = ({ onLoaded }) => {
  const [mode, setMode] = useState(QUERY_MODES.FILTERS)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [idsText, setIdsText] = useState('')
  const [createdByOptions, setCreatedByOptions] = useState([])
  const [verticalOptions, setVerticalOptions] = useState([])
  const [progress, setProgress] = useState(null)

  const destinations = useDestinations()
  const isLoading = progress !== null

  useEffect(() => {
    getCustomAudienceEnum('created_by.email')
      .then(setCreatedByOptions)
      .catch(() => {})
    getCustomAudienceEnum('vertical')
      .then((items) => setVerticalOptions(items.filter(Boolean)))
      .catch(() => {})
  }, [])

  const setFilter = (key) => (value) => setFilters((prev) => ({ ...prev, [key]: value }))

  // the local Radio reports the selected option by its label
  const handleModeRadio = (event) => {
    const selected = MODE_OPTIONS.find(({ label }) => label === event.target.value)
    if (selected) setMode(selected.value)
  }

  const modeRadioOptions = MODE_OPTIONS.map(({ value, label }) => ({
    label,
    checked: mode === value,
    disabled: isLoading,
  }))

  const buildContext = () => {
    const context = { filters: {} }
    if (filters.created_by) context.filters['created_by.email'] = filters.created_by.value
    if (filters.destination) context.filters['destinations._id'] = filters.destination._id
    if (filters.vertical) context.filters.vertical = filters.vertical.value
    if (filters.status) context.filters.status = filters.status.value
    if (filters.q.trim()) context.filters.q = filters.q.trim().toLowerCase()
    return context
  }

  const finishLoad = (audiences) => {
    setProgress(null)
    if (!audiences.length) {
      toast.error('No audiences found for this query.')
      return
    }
    onLoaded(audiences)
  }

  const handleLoad = async () => {
    setProgress({ loaded: 0, total: null })
    try {
      if (mode === QUERY_MODES.IDS) {
        const ids = parseIds(idsText)
        if (!ids.length) {
          setProgress(null)
          toast.error('Paste at least one audience ID.')
          return
        }
        const { audiences, missing } = await fetchAudiencesByIds(COLLECTION, ids, setProgress)
        if (missing.length) {
          toast.error(`${missing.length} ID(s) could not be loaded and were ignored.`)
        }
        finishLoad(audiences)
      } else {
        finishLoad(await fetchAllAudiences(COLLECTION, buildContext(), setProgress))
      }
    } catch (error) {
      setProgress(null)
      toast.error(error?.message || 'Error loading audiences.')
    }
  }

  return (
    <div className="bulk-edit-query">
      <section className="bulk-edit__card">
        <header className="bulk-edit__card-header">
          <h3>1. Find the audiences to move</h3>
        </header>

        <div className="bulk-edit-query__selectors">
          <div className="bulk-edit-query__selector">
            <span className="bulk-edit-query__selector-label">By:</span>
            <RadioGroup
              groupName="move-to-pdp-mode"
              className="bulk-edit-query__radios"
              options={modeRadioOptions}
              onChange={handleModeRadio}
            />
          </div>
        </div>

        <Choose>
          <When condition={mode === QUERY_MODES.FILTERS}>
            <p className="bulk-edit__hint">
              The query loads every matching audience creator audience (not just the first page)
              into your browser.
            </p>
            <div className="bulk-edit-query__filters">
              <Input
                label="Search"
                placeholder="Search for audiences"
                value={filters.q}
                onChange={setFilter('q')}
              />
              <Select
                label="Created by"
                useReactSelect
                isClearable
                isSearchable
                options={createdByOptions}
                value={filters.created_by}
                onChange={setFilter('created_by')}
              />
              <Select
                label="Destination"
                useReactSelect
                isClearable
                isSearchable
                options={destinations}
                value={filters.destination}
                onChange={setFilter('destination')}
              />
              <Select
                label="Status"
                useReactSelect
                isClearable
                options={COLLECTION.statuses}
                value={filters.status}
                onChange={setFilter('status')}
              />
              <Select
                label="Vertical"
                useReactSelect
                isClearable
                isSearchable
                options={verticalOptions}
                value={filters.vertical}
                onChange={setFilter('vertical')}
              />
            </div>
          </When>
          <Otherwise>
            <p className="bulk-edit__hint">
              Paste audience IDs separated by spaces, commas or line breaks.
            </p>
            <textarea
              className="bulk-edit-query__ids"
              aria-label="Audience IDs"
              rows={6}
              placeholder={'5eea1b7f37097dac6c04fa12\n632dec466420cb001a723e60'}
              value={idsText}
              onChange={(event) => setIdsText(event.target.value)}
            />
          </Otherwise>
        </Choose>

        <footer className="bulk-edit__card-footer">
          <If condition={isLoading}>
            <ProgressBar loaded={progress.loaded} total={progress.total} label="Loaded" />
          </If>
          <Button onClick={handleLoad} disabled={isLoading}>
            {isLoading ? 'Loading audiences…' : 'Load Audiences'}
          </Button>
        </footer>
      </section>
    </div>
  )
}

export default SelectStage
